/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/display-name */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AutoComplete, Checkbox } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { FormikProvider, useFormik } from 'formik';
import {
  AddRowButton, Form, Input, Table,
} from 'formik-antd';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import isEqual from 'lodash.isequal';
import { useStore } from '../../../store';
import { IColumn } from '../../../store/modules/report/i-column';
import { useActions } from '../../providers/actions-provider/actions.context';
import SecondaryButton from '../../lib/secondary-button';
import DeleteModal from '../../lib/delete-modal';
import LeavePageModal from '../../lib/leave-page-modal';
import checkPermission from '../../../utils/permissions';

interface IIndexs {
  oldIndex: number;
  newIndex: number;
}

const stringArrayToAutocompleteOptions = (stringArray: string[]) => (
  (stringArray || []).map((item) => ({ value: item, label: item }))
);

const SecondTab = (): JSX.Element => {
  // hook
  const { reportActions } = useActions();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { executedReport, allColumnsOfReport } = useStore();
  const { location: { pathname } } = useHistory();
  // ID from path logic
  const positionOfExec = pathname.indexOf('exec-');
  const positionOfID = pathname.indexOf('/', positionOfExec + 5);
  const id = pathname
    .slice(positionOfExec + 5, positionOfID === -1 ? pathname.length : positionOfID);
    // fields of reports + order moments for DND
  const { columns } = executedReport.data;
  const [sorted, setSorted] = useState(columns);
  const [editField, setEditField] = useState(0);
  const [fieldOrder, setFieldOrder] = useState(1);
  const [fieldOptions, setFieldOptions] = useState<{ value: string, label: string }[]>([]);

  useEffect(() => {
    reportActions.getExecutedReport(+id);
  }, []);

  useEffect(() => {
    reportActions.getAllColumnsForReport(executedReport.data.collection);
  }, [executedReport]);

  // DND wrappers, etc.
  const DragHandle = SortableHandle(() => (
    <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
  ));
  const SortItem = SortableElement((props: any) => <tr {...props} />);
  const SortContainer = SortableContainer((props: any) => <tbody {...props} />);
  const onSortEnd = ({ oldIndex, newIndex }: IIndexs) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMove(([] as IColumn[]).concat(sorted), oldIndex, newIndex)
        .filter((el) => !!el);
      const segment = newData.map((el, index) => {
        if (el.order !== index + 1 && el.id) {
          reportActions.editColumn(el.id, { ...el, order: index + 1 });
          return {
            ...el,
            order: index + 1,
          };
        }
        return { ...el };
      });
      setSorted(segment);
    }
  };
  const DraggableBodyRow = useMemo(() => function dragRow({ ...restProps }) {
    const index = sorted.findIndex(
      (x) => x.order === restProps['data-row-key'],
    );
    return <SortItem index={index} {...restProps} />;
  }, [sorted]);
  const DraggableContainer = useMemo(() => function dragContainer(props: JSX.IntrinsicAttributes &
  JSX.IntrinsicClassAttributes<React.Component<any, any, any>> & Readonly<any>
  & Readonly<{ children?: React.ReactNode; }>): any {
    return (
      <SortContainer
        useDragHandle
        helperClass="row-dragging"
        onSortEnd={onSortEnd}
        {...props}
      />
    );
  }, [sorted]);
  const components = {
    body: {
      wrapper: DraggableContainer,
      row: DraggableBodyRow,
    },
  };

  const initialField = {
    report_id: executedReport.data.id,
    order: fieldOrder,
    field: '',
    header: '',
    format: '',
    hidden: 0,
  };
  const formik = useFormik({
    initialValues: {
      fieldsTable: sorted,
    },
    enableReinitialize: true,
    onSubmit: async () => null,
  });
  const { setFieldValue, values: secondStepValues } = formik;

  const [modalVisible,
    setModalVisible] = useState<{toggle: boolean, id?: number}>({ toggle: false });

  // hook + memo
  useEffect(() => {
    if (!executedReport.loaded) {
      reportActions.getExecutedReport(+id);
    }
  }, []);
  useEffect(() => {
    const columnsAfterRemoving = [...columns].filter((el) => el.id !== modalVisible.id);
    secondStepValues.fieldsTable = columnsAfterRemoving;
    setSorted(columnsAfterRemoving);
  }, [columns.length]);

  const toggleModal = () => {
    setModalVisible({ toggle: !modalVisible, id: modalVisible.id });
  };
  const onConfirmDelete = async () => {
    if (modalVisible.id) {
      await reportActions.deleteColumn(modalVisible.id);
      await reportActions.getExecutedReport(+id);
      const columnsAfterRemoving = [...columns].filter((el) => el.id !== modalVisible.id);
      secondStepValues.fieldsTable = columnsAfterRemoving;
      setSorted(columnsAfterRemoving);
    }
    toggleModal();
  };

  const onFieldSearch = (searchText: string) => {
    const trimmedSearchText = searchText.trim();
    if (trimmedSearchText) {
      const opts = (allColumnsOfReport.data || []).filter(
        (column) => (
          column.toLowerCase().includes(trimmedSearchText.toLowerCase())),
      );
      setFieldOptions(stringArrayToAutocompleteOptions(opts));
    } else {
      setFieldOptions(stringArrayToAutocompleteOptions(allColumnsOfReport.data));
    }
  };

  // columns for table - need to make separate file for it
  const columnsHeaders = [
    {
      dataIndex: 'sort',
      width: '1%',
      className: 'drag-visible',
      render: () => (!editField && checkPermission('reports', 'write') && <DragHandle />),
    },
    {
      title: 'Header',
      dataIndex: 'header',
      key: 'header',
      render: (text: string, record: IColumn, index: number) => (record.order === editField ? (
        <Input
          type="text"
          name={`fieldsTable.${index}.header`}
          className="field__row-input"
          onChange={(e) => {
            setFieldValue(`fieldsTable.${index}.header`, e.target.value);
          }}
        />
      ) : (
        text
      )),
    },
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: '25%',
      render: (text: string, record: IColumn, index: number) => (record.order === editField ? (
        <AutoComplete
          dropdownClassName="table-field-options"
          value={text}
          options={fieldOptions}
          style={{ width: '100%' }}
          onSearch={onFieldSearch}
          onChange={(value:string) => {
            setFieldValue(`fieldsTable.${index}.field`, value);
          }}
          onFocus={() => {
            setFieldOptions(stringArrayToAutocompleteOptions(allColumnsOfReport.data));
          }}
        />
      ) : (
        text
      )),
    },
    {
      title: () => (editField !== 0 ? (
        <>
          <div>Format</div>
          <div className="optional-field">Optional</div>
        </>
      ) : 'Format'
      ),
      dataIndex: 'format',
      key: 'format',
      width: '30%',
      render: (text: string, record: IColumn, index: number) => (record.order === editField ? (
        <Input name={`fieldsTable.${index}.format`} type="text" className="field__row-input" />
      ) : (
        text
      )),
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      width: '25%',
      render: (text: string, record: IColumn, index: number) => (record.order === editField ? (
        <Input
          type="text"
          name={`fieldsTable.${index}.link`}
          className="field__row-input"
          onChange={(e) => {
            setFieldValue(`fieldsTable.${index}.link`, e.target.value);
          }}
        />
      ) : (
        text
      )),
    },
    {
      title: 'Hidden',
      dataIndex: 'hidden',
      key: 'hidden',
      width: '6%',
      render: (text: string, record: IColumn, index: number) => (
        <Checkbox
          checked={!!record.hidden}
          disabled={record.order !== editField || !checkPermission('reports', 'write')}
          onChange={(e) => {
            setFieldValue(`fieldsTable.${index}.hidden`, e.target.checked ? 1 : 0);
          }}
        />
      ),
    },
    {
      dataIndex: 'delete',
      key: 'delete',
      width: '5%',
      render: (text: string, record: IColumn) => (editField === record.order ? (
        <SecondaryButton
          type="button"
          className="ant-btn--small ant-btn-ghost button-cancel"
          onClick={() => {
            if (isEqual(record, initialField)) {
              secondStepValues.fieldsTable = secondStepValues.fieldsTable.slice(0, -1);
              setEditField(0);
            } else {
              setEditField(0);
            }
          }}
        >
          cancel
        </SecondaryButton>
      ) : (
        <SecondaryButton
          disabled={!checkPermission('reports', 'write')}
          className="button-cancel ant-btn--small ant-btn-ghost"
          onClick={() => {
            setModalVisible({ toggle: true, id: record.id });
          }}
        >
          delete
        </SecondaryButton>
      )),
    },
    {
      dataIndex: 'edit',
      key: 'edit',
      width: '5%',
      render: (text: string, record: IColumn) => (editField === record.order ? (
        <SecondaryButton
          type="submit"
          className="button-save ant-btn--small"
          disabled={!(record.header && record.field) || !checkPermission('reports', 'write')}
          onClick={async () => {
            setEditField(0);
            if (sorted.length === secondStepValues.fieldsTable.length && record.id) {
              await reportActions.editColumn(record.id, record);
              await reportActions.getExecutedReport(+id);
            } else {
              secondStepValues.fieldsTable.push(record);
              await reportActions.createColumn(record);
              await reportActions.getExecutedReport(+id);
            }
          }}
        >
          save
        </SecondaryButton>
      ) : (
        <SecondaryButton
          disabled={!checkPermission('reports', 'write')}
          type="button"
          className="button-edit ant-btn--small"
          onClick={() => setEditField(record.order)}
        >
          edit
        </SecondaryButton>
      )),
    },
  ];

  return (
    allColumnsOfReport ? (
      <>
        <FormikProvider value={formik}>
          {!!editField && <LeavePageModal />}
          <Form>
            <div className="create-column__wrapper" style={{ margin: 0 }}>
              <p className="create-column__title" />
              <AddRowButton
                name="fieldsTable"
                className="create-column__new"
                style={{ margin: 0 }}
                key={fieldOrder}
                disabled={!!editField || !checkPermission('reports', 'write')}
                createNewRow={() => {
                  let order;
                  if (columns.length) {
                    order = columns[columns.length - 1].order + 1;
                    setFieldOrder(order);
                    setEditField(order);
                  } else {
                    order = 1;
                    setFieldOrder(order);
                    setEditField(order);
                  }
                  return {
                    report_id: +id,
                    order,
                    field: '',
                    header: '',
                    format: '',
                    hidden: 0,
                  };
                }}
              >
                New Field
              </AddRowButton>
            </div>
            <Table
              name="fieldsTable"
              className="fields__table"
              columns={columnsHeaders}
              key="index"
              rowKey={(record) => record.order}
              pagination={false}
              loading={executedReport.loading}
              components={components}
            />
          </Form>
        </FormikProvider>
        <DeleteModal
          text="column"
          modalVisible={modalVisible.toggle}
          onConfirm={onConfirmDelete}
          onCancel={toggleModal}
        />
      </>
    ) : <></>
  );
};

SecondTab.displayName = 'SecondTab';
export default SecondTab;
